import { hash } from '@ember/helper';
import type Owner from '@ember/owner';
import Component from '@glimmer/component';

import type { ComponentLike } from '@glint/template';

import type { ValidationErrors } from 'webapp-types';

import type { ModelWithNotificationForPopup } from 'webapp/services/model-manager/notifications';

export type ValidateFn = () => ValidationErrors<string>;

export type SaveFn = () => Promise<unknown>;

export type SendPreviewFn = () => void;

export interface Args {
	model: ModelWithNotificationForPopup;
	submitInProgress: boolean;
	errors: ValidationErrors<string>;
	resetErrorData: (field?: string) => void;
}

export interface ChunkArgs {
	model: ModelWithNotificationForPopup;
	submitInProgress?: boolean;
	errors?: ValidationErrors<string>;
	resetErrorData?: (field?: string) => void;
}

export interface ChunkInterface {
	Args: ChunkArgs;
}

export interface MainChunkArgs extends Partial<ChunkArgs> {
	origin: string;
	setValidateFn: (fn: ValidateFn) => void;
	setSaveFn: (fn: SaveFn) => void;
	// registered by forms that support sending a preview (e.g. new scorecard)
	setSendPreviewFn?: (fn: SendPreviewFn) => void;
	// notify the parent when the form's content changes height (e.g. toggling advanced settings)
	onContentChange?: () => void;
}

export interface MainChunkInterface {
	Args: MainChunkArgs;
}

interface YieldedMainInterface {
	Args: {
		origin: string;
		setValidateFn: (fn: ValidateFn) => void;
		setSaveFn: (fn: SaveFn) => void;
		setSendPreviewFn?: (fn: SendPreviewFn) => void;
		onContentChange?: () => void;
	};
}

export interface Interface {
	Args: Args;
	Blocks: {
		default: [
			{
				title: ComponentLike;
				main: ComponentLike<YieldedMainInterface>;
			},
		];
	};
}

export default abstract class NotificationsEditFormBaseComponent extends Component<Interface> {
	abstract get titleComponent(): ComponentLike<ChunkInterface>;

	abstract get mainComponent(): ComponentLike<MainChunkInterface>;

	abstract get model(): ModelWithNotificationForPopup;

	abstract get errors(): ValidationErrors<string>;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.args.resetErrorData();
	}

	<template>
		{{yield
			(hash
				title=(component
					this.titleComponent model=this.model submitInProgress=@submitInProgress
				)
				main=(component
					this.mainComponent
					model=this.model
					errors=this.errors
					resetErrorData=@resetErrorData
					submitInProgress=@submitInProgress
				)
			)
		}}
	</template>
}
